// Shapes an unhandled error into 'Client Error' properties. The mobile app sends
// the same property names, so one Mixpanel report can segment both clients.

const CLIENT_ERROR_MESSAGE_LIMIT = 200;
// Mixpanel truncates string properties at 255 characters.
const CLIENT_ERROR_STACK_LIMIT = 255;

// Strip the values most likely to carry secrets out of an error string before
// it is sent as an analytics property: URL query strings and long token-like
// blobs.
const scrubSecrets = (text: string): string =>
  text.replace(/\?[^\s'")]*/g, '?[redacted]').replace(/[A-Za-z0-9_-]{24,}/g, '[token]');

// Messages can also quote what the voter typed, so emails go too. Stacks skip
// this step: Safari frames ("fn@file.js") read as email addresses.
export const scrubErrorMessage = (message: string): string =>
  scrubSecrets(message)
    .replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '[email]')
    .slice(0, CLIENT_ERROR_MESSAGE_LIMIT);

// V8 frames start with "at "; Safari/Firefox frames end "@file:line:col" or
// "@[native code]". Anchoring the "@" form keeps the stack's "Error: <message>"
// line out, so an email in the message can't bypass scrubErrorMessage.
const FRAME = /^at\s|@(?:\S*:\d+:\d+|\[native code\])$/;

/** The top stack frames, scrubbed and joined on one line; undefined without a stack. */
export const formatErrorStack = (error: unknown): string | undefined => {
  const stack = error instanceof Error ? error.stack : undefined;
  if (!stack) return undefined;
  const frames = stack
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => FRAME.test(line));
  if (frames.length === 0) return undefined;
  return scrubSecrets(frames.join(' | ')).slice(0, CLIENT_ERROR_STACK_LIMIT);
};

/**
 * Who threw the error, so reports and alerts can keep VoteMate's own bugs
 * apart from code that only runs inside our page:
 * - `app`: our bundle.
 * - `host_bridge`: an in-app browser's native bridge (Facebook, Instagram, …)
 *   failing, e.g. after the user backgrounds the app.
 * - `cross_origin`: "Script error." — a cross-origin script whose details the
 *   browser hides.
 * - `third_party`: a script served from another origin.
 * - `injected`: attributed to the page document itself. The page ships no
 *   inline script, so this is code the browser or an extension injected.
 * - `unknown`: no file to attribute (most promise rejections).
 */
export type ErrorOrigin =
  'app' | 'host_bridge' | 'cross_origin' | 'third_party' | 'injected' | 'unknown';

const HOST_BRIDGE =
  /Java object is gone|Java exception was raised during method invocation|webkit\.messageHandlers/;
const OPAQUE_SCRIPT_ERROR = /^Script error\.?$/;

const parseUrl = (value: string, base: string): URL | undefined => {
  try {
    return new URL(value, base);
  } catch {
    return undefined;
  }
};

export const classifyErrorOrigin = ({
  message,
  filename,
  stack,
  pageUrl,
}: {
  message: string;
  filename?: string;
  stack?: string;
  pageUrl: string;
}): ErrorOrigin => {
  if (HOST_BRIDGE.test(message)) return 'host_bridge';
  if (OPAQUE_SCRIPT_ERROR.test(message)) return 'cross_origin';
  const page = new URL(pageUrl);
  const file = filename ? parseUrl(filename, pageUrl) : undefined;
  if (file) {
    if (file.origin !== page.origin) return 'third_party';
    if (file.pathname === page.pathname) return 'injected';
    return 'app';
  }
  if (stack?.includes(page.origin)) return 'app';
  return 'unknown';
};

// Same-origin files keep only their path; others keep host and path.
const formatErrorFile = (filename: string, pageUrl: string): string | undefined => {
  const file = parseUrl(filename, pageUrl);
  if (!file) return undefined;
  const path = file.origin === new URL(pageUrl).origin ? file.pathname : file.host + file.pathname;
  return scrubSecrets(path).slice(0, CLIENT_ERROR_MESSAGE_LIMIT);
};

export type ErrorLocation = { filename?: string; lineno?: number; colno?: number };

/** 'Client Error' properties for an error or rejection reason. Unset values are omitted. */
export const describeClientError = (
  raw: unknown,
  { filename, lineno, colno }: ErrorLocation = {},
  pageUrl: string = window.location.href
): Record<string, string | number> => {
  const message = raw instanceof Error ? raw.message : String(raw ?? 'Unknown error');
  const rawStack = raw instanceof Error ? raw.stack : undefined;
  const stack = formatErrorStack(raw);
  const file = filename ? formatErrorFile(filename, pageUrl) : undefined;
  return {
    error_message: scrubErrorMessage(message),
    error_origin: classifyErrorOrigin({ message, filename, stack: rawStack, pageUrl }),
    ...(file && { error_file: file }),
    ...(lineno && { error_line: lineno }),
    ...(colno && { error_col: colno }),
    ...(stack && { error_stack: stack }),
  };
};
